<template>
  <aside
    ref="sidebarEl"
    class="sidebar d-flex flex-column"
    :class="{ 'sidebar-icon-only': sidebarIconOnly, collapsed: sidebarIconOnly }"
    :aria-label="t('aria.sidebar')"
  >
    <button
      type="button"
      class="sidebar-collapse-btn"
      :data-tooltip="sidebarIconOnly ? t('expand') : undefined"
      :aria-label="sidebarIconOnly ? t('expand') : t('collapse')"
      :aria-expanded="!sidebarIconOnly"
      @click="toggleSidebarCollapsed"
    >
      <i
        class="bi bi-layout-sidebar-inset"
        aria-hidden="true"
      ></i>
      <span class="collapse-text">{{ t('collapse') }}</span>
    </button>

    <div
      ref="viewportEl"
      class="sidebar__viewport"
    >
      <div
        ref="trackEl"
        class="sidebar__track"
        :style="trackStyle"
      >
        <nav class="sidebar__nav">
          <ul class="nav flex-column">
            <SidebarItem
              v-for="navRoute in routes"
              :key="navRoute.name || navRoute.path"
              :item="navRoute"
            />
          </ul>
        </nav>
        <SidebarFooter />
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import { useUiStore } from '@/stores/ui'
import { canViewSidebarRoute } from '@/lib/routeAccess'
import SidebarItem from '@/components/sidebar/sidebarItem.vue'
import SidebarFooter from '@/components/sidebar/sidebarFooter.vue'

const { t } = useI18n()
const $router = useRouter()
const userStore = useUserStore()
const uiStore = useUiStore()
const { sidebarIconOnly } = storeToRefs(uiStore)
const { toggleSidebarCollapsed } = uiStore

const sidebarEl = ref(null)
const viewportEl = ref(null)
const trackEl = ref(null)
/** Сдвиг трека вверх (≤ 0); 0 = верх меню, min = низ меню у нижнего края окна. */
const shift = ref(0)

const visibilityCheck = r => canViewSidebarRoute(r, userStore)
const routes = computed(() =>
  $router.options.routes.filter(r => visibilityCheck(r))
)

const trackStyle = computed(() => ({
  transform: `translateY(${shift.value}px)`,
}))

function minShift() {
  const view = viewportEl.value
  const track = trackEl.value
  if (!view || !track) return 0
  return Math.min(0, view.clientHeight - track.offsetHeight)
}

function clampShift(y) {
  return Math.max(minShift(), Math.min(0, y))
}

function syncShift() {
  shift.value = clampShift(shift.value)
}

/** Колесо над меню двигает только меню (до своего низа), реестр справа не трогаем. */
function onSidebarWheel(e) {
  e.preventDefault()
  e.stopPropagation()
  if (minShift() >= 0) {
    shift.value = 0
    return
  }
  shift.value = clampShift(shift.value - e.deltaY)
}

let resizeObserver = null

onMounted(() => {
  sidebarEl.value?.addEventListener('wheel', onSidebarWheel, { passive: false })
  resizeObserver = new ResizeObserver(() => {
    syncShift()
  })
  if (viewportEl.value) resizeObserver.observe(viewportEl.value)
  if (trackEl.value) resizeObserver.observe(trackEl.value)
})

onBeforeUnmount(() => {
  sidebarEl.value?.removeEventListener('wheel', onSidebarWheel)
  resizeObserver?.disconnect()
  resizeObserver = null
})

watch(sidebarIconOnly, async () => {
  shift.value = 0
  await nextTick()
  syncShift()
})
</script>

<i18n>
{
  "ru": {
    "aria": {
      "sidebar": "Разделы приложения"
    },
    "collapse": "Свернуть",
    "expand": "Развернуть"
  },
  "en": {
    "aria": {
      "sidebar": "Application sections"
    },
    "collapse": "Collapse",
    "expand": "Expand"
  }
}
</i18n>
